import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Settings, Shield, Save, Mail, Trash2, Loader2, Moon, Sun, Monitor, Database, Download, Upload, Send, Users } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Parametres = ({ user }) => {
  const { darkMode, themeMode, setThemeMode } = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    password: '',
    confirmText: ''
  });

  // Backup state
  const [backupConfig, setBackupConfig] = useState({
    enabled: false,
    frequency: 'weekly',
    custom_day: 1,
    time_of_day: '02:00',
    recipient_user_ids: [],
    template_id: null,
  });
  const [backupStatus, setBackupStatus] = useState(null);
  const [savingBackup, setSavingBackup] = useState(false);
  const [testingBackup, setTestingBackup] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    currency: 'DT',
    currency_symbol: 'DT',
    max_users: 10,
    items_per_page: 10,
    chart_period_months: 12,
    theme_color: '#10B981',
  });
  const [profileData, setProfileData] = useState({
    email: '',
    nom: '',
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchSettings();
    fetchProfile();
    if (isAdmin) {
      fetchBackupConfig();
      fetchOrgUsers();
      fetchEmailTemplates();
    }
  }, [isAdmin]);

  const fetchOrgUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setOrgUsers(response.data.filter(u => u.is_active !== false));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates/`);
      setEmailTemplates(response.data.filter(t => t.type === 'backup'));
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchBackupConfig = async () => {
    try {
      const response = await axios.get(`${API}/backup-config`);
      setBackupConfig({
        enabled: response.data.enabled || false,
        frequency: response.data.frequency || 'weekly',
        custom_day: response.data.custom_day || 1,
        time_of_day: response.data.time_of_day || '02:00',
        recipient_user_ids: response.data.recipient_user_ids || [],
        template_id: response.data.template_id || null,
      });
      fetchBackupStatus();
    } catch (error) {
      console.error('Error fetching backup config:', error);
    }
  };

  const fetchBackupStatus = async () => {
    try {
      const response = await axios.get(`${API}/backup-config/status`);
      setBackupStatus(response.data);
    } catch (error) {
      console.error('Error fetching backup status:', error);
    }
  };

  const handleBackupSubmit = async (e) => {
    e.preventDefault();
    setSavingBackup(true);
    try {
      await axios.post(`${API}/backup-config`, backupConfig);
      toast.success('Configuration de backup enregistrée');
      fetchBackupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSavingBackup(false);
    }
  };

  const handleTestBackup = async () => {
    if (!backupConfig.recipient_user_ids || backupConfig.recipient_user_ids.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    setTestingBackup(true);
    try {
      const response = await axios.post(`${API}/backup-config/test`, { 
        recipient_user_ids: backupConfig.recipient_user_ids 
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setTestingBackup(false);
    }
  };

  const handleResetBackupLastSent = async () => {
    try {
      const response = await axios.post(`${API}/backup-config/reset-last-sent`);
      toast.success(response.data.message);
      fetchBackupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Envoyer la sauvegarde maintenant aux destinataires configurés ?')) return;
    setSendingNow(true);
    try {
      const response = await axios.post(`${API}/backup-config/send-now`);
      toast.success(response.data.message);
      fetchBackupStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingNow(false);
    }
  };

  const toggleBackupRecipient = (userId) => {
    setBackupConfig(prev => {
      const newRecipients = prev.recipient_user_ids.includes(userId)
        ? prev.recipient_user_ids.filter(id => id !== userId)
        : [...prev.recipient_user_ids, userId];
      return { ...prev, recipient_user_ids: newRecipients };
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/backup/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mzarati_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export téléchargé avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Veuillez sélectionner un fichier JSON');
      event.target.value = '';
      return;
    }

    if (!window.confirm('⚠️ Attention: L\'import va REMPLACER toutes vos données actuelles. Êtes-vous sûr?')) {
      event.target.value = '';
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/backup/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setFormData({
        currency: response.data.currency,
        currency_symbol: response.data.currency_symbol,
        max_users: response.data.max_users,
        items_per_page: response.data.items_per_page || 10,
        chart_period_months: response.data.chart_period_months || 12,
        theme_color: response.data.theme_color || '#10B981',
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setProfileData({
        email: response.data.email,
        nom: response.data.nom,
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/settings`, formData);
      toast.success('Paramètres enregistrés avec succès');
      fetchSettings();
      // Recharger la page pour appliquer les changements de devise
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/auth/profile`, profileData);
      toast.success('Profil mis à jour avec succès');
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setSaving(true);

    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Mot de passe changé avec succès');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    const emailToTest = testEmailRecipient || user.email;
    if (!emailToTest) {
      toast.error('Veuillez saisir une adresse email pour le test');
      return;
    }

    setTestingEmail(true);

    try {
      await axios.post(`${API}/test-email`, {
        recipient_email: emailToTest,
      });
      toast.success('Email de test envoyé avec succès !');
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email de test');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    if (!deleteConfirmation.password || deleteConfirmation.confirmText !== 'SUPPRIMER') {
      toast.error('Veuillez remplir tous les champs de confirmation');
      return;
    }

    if (!window.confirm('⚠️ ATTENTION ! Cette action est IRRÉVERSIBLE. Toutes vos données (moutons, dépenses, transactions, etc.) seront DÉFINITIVEMENT supprimées. Confirmez-vous ?')) {
      return;
    }

    setDeletingAccount(true);

    try {
      await axios.delete(`${API}/auth/delete-account`, {
        data: {
          password: deleteConfirmation.password,
          confirmation_text: deleteConfirmation.confirmText
        }
      });
      
      toast.success('Organisation supprimée avec succès');
      
      // Déconnexion forcée
      localStorage.removeItem('token');
      window.location.href = '/login';
      
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingAccount(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="parametres-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Paramètres & Profil</h1>
        <p className="text-gray-600 mt-1">Gérez vos informations personnelles et les paramètres de l'application</p>
      </div>

      {/* Mon Profil */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-xl">Mon Profil</CardTitle>
          <CardDescription>Modifiez vos informations personnelles</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="profile_nom">Nom complet</Label>
              <Input
                id="profile_nom"
                value={profileData.nom}
                onChange={(e) => setProfileData({ ...profileData, nom: e.target.value })}
                data-testid="profile-nom-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profile_email">Email</Label>
              <Input
                id="profile_email"
                type="email"
                value={profileData.email}
                onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                data-testid="profile-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
              data-testid="save-profile-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer le profil'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Changer le mot de passe */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-xl">Changer le mot de passe</CardTitle>
          <CardDescription>Modifiez votre mot de passe de connexion</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Mot de passe actuel</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                required
                data-testid="current-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new_password">Nouveau mot de passe</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">Minimum 6 caractères</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmer le mot de passe</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                required
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
              data-testid="change-password-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Changement...' : 'Changer le mot de passe'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Paramètres de l'application (Admin seulement) */}
      {isAdmin && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Settings className="w-5 h-5 text-slate-700 dark:text-slate-300" />
              <span>Paramètres de l'application</span>
            </CardTitle>
            <CardDescription>Configuration générale (réservé à l'administrateur)</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="currency">Devise</Label>
                  <Input
                    id="currency"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    placeholder="Ex: DT, EUR, USD"
                    data-testid="currency-input"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Code de la devise (ex: DT, EUR)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currency_symbol">Symbole de la devise</Label>
                  <Input
                    id="currency_symbol"
                    value={formData.currency_symbol}
                    onChange={(e) => setFormData({ ...formData, currency_symbol: e.target.value })}
                    placeholder="Ex: DT, €, $"
                    data-testid="currency-symbol-input"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Symbole affiché (ex: DT, €)</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="max_users">Nombre maximum d'utilisateurs</Label>
                <Input
                  id="max_users"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_users}
                  onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
                  data-testid="max-users-input"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">Nombre maximum d'utilisateurs pouvant être invités (1-100)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="items_per_page">Éléments par page</Label>
                <Select 
                  value={(formData.items_per_page || 10).toString()} 
                  onValueChange={(value) => setFormData({ ...formData, items_per_page: parseInt(value) })}
                >
                  <SelectTrigger data-testid="items-per-page-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 éléments</SelectItem>
                    <SelectItem value="10">10 éléments</SelectItem>
                    <SelectItem value="15">15 éléments</SelectItem>
                    <SelectItem value="20">20 éléments</SelectItem>
                    <SelectItem value="25">25 éléments</SelectItem>
                    <SelectItem value="50">50 éléments</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 dark:text-gray-400">Nombre d'éléments affichés par page dans les tableaux</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="chart_period_months">Période des graphiques (tableau de bord)</Label>
                <Select 
                  value={(formData.chart_period_months || 12).toString()} 
                  onValueChange={(value) => setFormData({ ...formData, chart_period_months: parseInt(value) })}
                >
                  <SelectTrigger data-testid="chart-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 mois</SelectItem>
                    <SelectItem value="6">6 mois</SelectItem>
                    <SelectItem value="12">12 mois</SelectItem>
                    <SelectItem value="24">24 mois</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 dark:text-gray-400">Période affichée dans les graphiques du tableau de bord</p>
              </div>

              <div className="space-y-2">
                <Label>Mode d'affichage</Label>
                <div className="flex gap-2">
                  {[
                    { key: 'light', label: 'Clair', icon: Sun, color: 'text-amber-500' },
                    { key: 'dark', label: 'Sombre', icon: Moon, color: 'text-indigo-400' },
                    { key: 'system', label: 'Système', icon: Monitor, color: 'text-gray-400' },
                  ].map(({ key, label, icon: Icon, color }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setThemeMode(key)}
                      data-testid={`theme-mode-${key}`}
                      className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        themeMode === key
                          ? 'border-slate-500 bg-slate-50 dark:bg-slate-900/30 dark:bg-slate-900/30'
                          : 'border-gray-200 dark:border-gray-800 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${themeMode === key ? 'text-slate-700 dark:text-slate-300' : color}`} />
                      <span className={`text-sm font-medium ${themeMode === key ? 'text-slate-800 dark:text-slate-200 dark:text-slate-400' : 'text-gray-600 dark:text-gray-400'}`}>
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h3 className="font-medium text-gray-900 dark:text-white mb-4">Configuration Email (SMTP)</h3>
                <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    La configuration SMTP est gérée via les variables d'environnement du serveur (.env). Contactez votre administrateur système pour modifier ces paramètres.
                  </p>
                  <div className="pt-2 space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="test-email">Tester l'envoi d'email</Label>
                      <Input
                        id="test-email"
                        type="email"
                        value={testEmailRecipient}
                        onChange={(e) => setTestEmailRecipient(e.target.value)}
                        placeholder={user.email || "votre@email.com"}
                        data-testid="test-email-recipient-input"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Laissez vide pour utiliser votre email de profil ({user.email})
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTestEmail}
                      disabled={testingEmail}
                      className="flex items-center gap-2 w-full"
                      data-testid="test-email-button"
                    >
                      <Mail className="w-4 h-4" />
                      {testingEmail ? 'Envoi en cours...' : 'Tester la configuration email'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Aperçu</h3>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <p>Exemple de prix : <span className="font-semibold">1500 {formData.currency_symbol}</span></p>
                  <p>Limite utilisateurs : <span className="font-semibold">{formData.max_users} utilisateurs maximum</span></p>
                  <p>Pagination : <span className="font-semibold">{formData.items_per_page} éléments par page</span></p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
                  data-testid="save-settings-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({
                    currency: settings.currency,
                    currency_symbol: settings.currency_symbol,
                    max_users: settings.max_users,
                    items_per_page: settings.items_per_page || 10,
                  })}
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isAdmin && (
        <Card className="border-0 shadow-md border-orange-200">
          <CardHeader>
            <CardTitle className="text-xl text-orange-600">Informations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <p>• Les changements de devise seront appliqués après rechargement de la page</p>
              <p>• La limite d'utilisateurs empêchera l'invitation de nouveaux membres au-delà du nombre défini</p>
              <p>• Seul l'administrateur peut modifier les paramètres de l'application</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backup & Restauration */}
      {isAdmin && (
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-xl flex items-center space-x-2">
              <Database className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              <span>Sauvegarde & Restauration</span>
            </CardTitle>
            <CardDescription>Sauvegardez et restaurez vos données automatiquement ou manuellement</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Manual Export/Import */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-200 dark:border-slate-700" data-testid="backup-export-section">
                <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export Manuel
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                  Téléchargez toutes vos données au format JSON
                </p>
                <Button
                  onClick={handleExport}
                  disabled={exporting}
                  className="w-full bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
                  data-testid="backup-export-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {exporting ? 'Export en cours...' : 'Exporter les données'}
                </Button>
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800" data-testid="backup-import-section">
                <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Import de données
                </h4>
                <p className="text-sm text-amber-600 dark:text-amber-300 mb-3">
                  Restaurez depuis un fichier de backup JSON
                </p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImport}
                  className="hidden"
                  data-testid="backup-import-file-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  variant="outline"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
                  data-testid="backup-import-btn"
                >
                  {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {importing ? 'Import en cours...' : 'Importer un fichier'}
                </Button>
              </div>
            </div>

            {/* Automatic Backup Configuration */}
            <div className="border-t pt-6">
              <h4 className="font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Backup Automatique par Email
              </h4>

              {/* Status Display */}
              {backupStatus && backupStatus.enabled && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800" data-testid="backup-status-display">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="font-medium text-green-700 dark:text-green-300">Backup automatique actif</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-600 dark:text-green-400">
                    {backupStatus.next_scheduled && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">Prochaine exécution: </span>
                        <span className="font-medium">{backupStatus.next_scheduled}</span>
                      </div>
                    )}
                    {backupStatus.last_sent && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Dernier envoi: </span>
                        <span className="font-medium">{new Date(backupStatus.last_sent).toLocaleString('fr-FR')}</span>
                        <button
                          type="button"
                          onClick={handleResetBackupLastSent}
                          className="text-xs text-slate-600 hover:text-slate-700 dark:text-slate-400 underline ml-2"
                        >
                          (Réinitialiser)
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <form onSubmit={handleBackupSubmit} className="space-y-4">
                {/* Enable Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg border border-slate-200 dark:border-slate-700" data-testid="backup-toggle-section">
                  <div>
                    <Label className="text-base font-medium">Activer le backup automatique</Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Recevoir une sauvegarde par email</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBackupConfig({ ...backupConfig, enabled: !backupConfig.enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      backupConfig.enabled ? 'bg-slate-800 dark:bg-slate-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                    data-testid="backup-toggle"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      backupConfig.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {backupConfig.enabled && (
                  <>
                    {/* Frequency */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Fréquence</Label>
                        <Select
                          value={backupConfig.frequency}
                          onValueChange={(value) => setBackupConfig({ ...backupConfig, frequency: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Tous les jours</SelectItem>
                            <SelectItem value="weekly">Toutes les semaines</SelectItem>
                            <SelectItem value="monthly">Tous les mois</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Heure d'envoi (heure de Paris)</Label>
                        <Input
                          type="time"
                          value={backupConfig.time_of_day}
                          onChange={(e) => setBackupConfig({ ...backupConfig, time_of_day: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Day Selection */}
                    {backupConfig.frequency === 'weekly' && (
                      <div className="space-y-2">
                        <Label>Jour de la semaine</Label>
                        <Select
                          value={backupConfig.custom_day?.toString()}
                          onValueChange={(value) => setBackupConfig({ ...backupConfig, custom_day: parseInt(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Lundi</SelectItem>
                            <SelectItem value="2">Mardi</SelectItem>
                            <SelectItem value="3">Mercredi</SelectItem>
                            <SelectItem value="4">Jeudi</SelectItem>
                            <SelectItem value="5">Vendredi</SelectItem>
                            <SelectItem value="6">Samedi</SelectItem>
                            <SelectItem value="7">Dimanche</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {backupConfig.frequency === 'monthly' && (
                      <div className="space-y-2">
                        <Label>Jour du mois</Label>
                        <Select
                          value={backupConfig.custom_day?.toString()}
                          onValueChange={(value) => setBackupConfig({ ...backupConfig, custom_day: parseInt(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(28)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>
                                {i + 1}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Template Selector */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Template Email
                      </Label>
                      <Select
                        value={backupConfig.template_id || "default"}
                        onValueChange={(value) => setBackupConfig({ ...backupConfig, template_id: value === "default" ? null : value })}
                      >
                        <SelectTrigger data-testid="backup-template-select">
                          <SelectValue placeholder="Template par défaut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">📧 Template par défaut</SelectItem>
                          {emailTemplates.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              🎨 {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Recipients */}
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Destinataires
                      </Label>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border max-h-40 overflow-y-auto space-y-2">
                        {orgUsers.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">Aucun utilisateur</p>
                        ) : (
                          orgUsers.map((u) => (
                            <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-white dark:hover:bg-gray-700 rounded cursor-pointer">
                              <Checkbox
                                checked={backupConfig.recipient_user_ids.includes(u.id)}
                                onCheckedChange={() => toggleBackupRecipient(u.id)}
                              />
                              <div className="flex-1">
                                <div className="font-medium text-sm">{u.nom || u.email}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                      {backupConfig.recipient_user_ids.length === 0 && (
                        <p className="text-xs text-amber-600">⚠️ Sélectionnez au moins un destinataire</p>
                      )}
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    disabled={savingBackup}
                    className="bg-slate-800 hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
                    data-testid="backup-save-btn"
                  >
                    {savingBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                  
                  {backupConfig.enabled && backupConfig.recipient_user_ids.length > 0 && (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleTestBackup}
                        disabled={testingBackup}
                        className="border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                        data-testid="backup-test-btn"
                      >
                        {testingBackup ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Tester
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleSendNow}
                        disabled={sendingNow}
                        className="border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-900/30"
                        data-testid="backup-send-now-btn"
                      >
                        {sendingNow ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Envoyer maintenant
                      </Button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Zone Dangereuse - Suppression de compte */}
      {isAdmin && (
        <Card className="border-2 border-red-200 shadow-md">
          <CardHeader className="bg-red-50">
            <CardTitle className="text-xl text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Zone Dangereuse
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-medium text-red-800 mb-2">⚠️ Supprimer définitivement votre organisation</h3>
                <div className="text-sm text-red-700 space-y-1">
                  <p>• <strong>TOUTES</strong> vos données seront supprimées (moutons, dépenses, transactions, etc.)</p>
                  <p>• <strong>TOUS</strong> vos utilisateurs perdront l'accès</p>
                  <p>• Cette action est <strong>IRRÉVERSIBLE</strong></p>
                </div>
              </div>

              <form onSubmit={handleDeleteAccount} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="delete-password">Votre mot de passe *</Label>
                    <Input
                      id="delete-password"
                      type="password"
                      placeholder="Confirmer avec votre mot de passe"
                      value={deleteConfirmation.password}
                      onChange={(e) => setDeleteConfirmation({ ...deleteConfirmation, password: e.target.value })}
                      data-testid="delete-password-input"
                      className="border-red-300 focus:border-red-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delete-confirm">Tapez "SUPPRIMER" *</Label>
                    <Input
                      id="delete-confirm"
                      type="text"
                      placeholder="SUPPRIMER"
                      value={deleteConfirmation.confirmText}
                      onChange={(e) => setDeleteConfirmation({ ...deleteConfirmation, confirmText: e.target.value })}
                      data-testid="delete-confirm-input"
                      className={`border-red-300 focus:border-red-500 ${
                        deleteConfirmation.confirmText === 'SUPPRIMER' ? 'bg-red-50 text-red-800' : ''
                      }`}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={deletingAccount || !deleteConfirmation.password || deleteConfirmation.confirmText !== 'SUPPRIMER'}
                  className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                  data-testid="delete-account-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {deletingAccount ? 'Suppression en cours...' : 'SUPPRIMER DÉFINITIVEMENT'}
                </Button>
              </form>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Parametres;
